import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDocumentHistory } from "~/api/v2/useDocumentHistory";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "./NoResult";
import AvatarWithFallback from "./AvatarWithFallback";
import { formatDate } from "~/helpers/date";
import { Badge } from "~/atoms/badge";
import { type DocumentHistoryEvent } from "~/api/types";

function getEventLabel(event: DocumentHistoryEvent, t: TFunction): string {
  return t(($) => $.commons.documentHistory.events[event.type], event.details);
}

interface Props {
  documentId: number;
}

export function DocumentAuditTrailTimeline({ documentId }: Props) {
  const { t } = useTranslation();

  const { data: historyData, isLoading: isLoadingHistory } =
    useDocumentHistory(documentId);

  if (isLoadingHistory) {
    return <CenterSpinner withText />;
  }

  if (!historyData) {
    return <NoResult message={t(($) => $.commons.noItems)} />;
  }

  return (
    <div className="bg-card rounded-lg border xl:w-2/3">
      <ul className="divide-y">
        {historyData.history.map((event, i) => (
          <li className="p-4" key={i}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{getEventLabel(event, t)}</p>

                {event.actor && (
                  <div className="flex items-center gap-2">
                    <AvatarWithFallback user={event.actor} size="xs" />
                    <p className="text-muted-foreground text-sm">
                      {event.actor.firstName} {event.actor.lastName}
                      {" • "}
                      {formatDate(event.timestamp, "dateTime")}
                    </p>
                  </div>
                )}
              </div>

              {event.version && (
                <Badge variant="outline">
                  {t(($) => $.commons.workflow.versionOrder, {
                    order: event.version.order,
                  })}
                </Badge>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
